import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { geoMercator, geoPath, geoGraticule10, geoInterpolate } from 'd3-geo'
import { select } from 'd3-selection'
import { zoom as d3zoom, zoomIdentity, type ZoomBehavior, type ZoomTransform } from 'd3-zoom'
import * as topojson from 'topojson-client'
import { ClockDial, readClockFace, type ClockFace } from '@/components/clock/ClockDial'
import '@/styles/map.css'
import { formatDateTime } from '@/lib/dateUtils'

export type MapHealth = 'green' | 'amber' | 'red' | 'grey'

/**
 * Map surface palette.
 *
 * 'theme' derives land and sea from the app's own design tokens, so the map restyles with the
 * Light/Dark toggle and cannot drift from the design system. 'geographic' is the conventional
 * physical-map reading — green land, blue sea — for people who want the map to look like a map.
 *
 * Stored per browser rather than per screen: an engineer who wants a geographic map wants it on
 * every map, not just the one they happened to set it on.
 */
export type MapPalette = 'theme' | 'geographic'

const PALETTE_KEY = 'vsMap.palette'

function readMapPalette(): MapPalette {
    try {
        return localStorage.getItem(PALETTE_KEY) === 'geographic' ? 'geographic' : 'theme'
    } catch {
        return 'theme'
    }
}

export interface MapLocation {
    id: string
    name: string
    /** [longitude, latitude] — GeoJSON order. Absent when the location cannot be plotted. */
    coords?: [number, number] | null
    category: string
    place: string
    objects: number
    /**
     * Recovery context, when the map is showing a recovery set rather than the estate.
     * Optional so the same component serves both — the tooltip shows these only when given.
     */
    engineers?: number | null
    earliestStart?: string | null
    highestRta?: string | null
    /**
     * IANA zone, e.g. 'Europe/London'. Drives the hover clock; absent means no clock for this
     * location rather than a guess.
     *
     * NEVER derived from longitude. India is +05:30, China runs one zone across five zones' worth
     * of longitude, and no arithmetic on a meridian knows about DST. It comes from
     * WorldLocations.timezone, which is populated for 13,802 cities and covers every estate
     * location that has coordinates.
     */
    timeZone?: string | null
}

export interface MapVault {
    id: string
    vaultId: string
    name: string
    /** The AWS REGION's coordinates, not the city served. */
    coords: [number, number]
    region: string
    status: 'active' | 'inactive'
    objects: number
    versions: number
    size: string
}

export interface VaultCopy {
    locationId: string
    vaultId: string
    /** Path attributes. Real, per-edge, and shown on hover — they do not colour the line. */
    bandwidth?: number | null
    latencyPercentage?: number | null
    tcpPercentage?: number | null
    encryptionPercentage?: number | null
}

export interface GlobalMapProps {
    locations?: MapLocation[]
    vaults?: MapVault[]
    copies?: VaultCopy[]
    detail?: 'medium' | 'high'
    height?: number
    showLegend?: boolean
    showZoomControls?: boolean
    /** Hide the palette switch on surfaces too small to spare the control. */
    showPaletteControl?: boolean
    onSelect?: (node: MapLocation | MapVault, kind: 'location' | 'vault') => void
}

/** Mercator past ±72° stretches to nothing useful, so the world is cropped there. */
const BOX = {
    type: 'Polygon' as const,
    coordinates: [[[-180, -72], [180, -72], [180, 72], [-180, 72], [-180, -72]]],
}

const W = 960

const TIER = {
    medium: { file: '/geo/countries-50m.json', coast: 0.6, coastOpacity: 1.0, border: 0.5, borderOpacity: 0.85 },
    /**
     * At global zoom the 1:10m coastline averages ~0.6px between vertices. A 0.6px stroke on
     * 0.6px spacing overlaps itself and darkens the land into a different colour, so the finer
     * tier gets a finer, softer line.
     */
    high: { file: '/geo/countries-10m.json', coast: 0.35, coastOpacity: 0.85, border: 0.3, borderOpacity: 0.55 },
} as const

interface Geometry { land: any; borders: any; graticule: any }

const FAILED_CLOCK = { h: 0, m: 0, offset: 0, ok: false as const }

/**
 * An IANA id must look like Region/City.
 *
 * Intl is far more permissive than it looks, and every value it quietly accepts here would
 * render a CONFIDENT clock from a value that means "no zone configured": 'Factory' and 'Zulu'
 * (tzdata's own placeholders) resolve to UTC, as do bare 'GMT'/'UTC' and lowercase ids, and
 * browsers — though not Node — also accept bare offsets like '+05:30', which pin a site to a
 * fixed offset that is silently an hour wrong for half the year.
 *
 * All 13,802 WorldLocations rows currently hold a proper Region/City id, so this rejects
 * nothing real today. It is here so that a bad row later shows NO clock rather than a
 * plausible one.
 */
const IANA_SHAPE = /^[A-Za-z][A-Za-z0-9_+-]*(?:\/[A-Za-z0-9_+-]+)+$/

/**
 * Local wall-clock time AND the zone's current UTC offset, from one formatted instant.
 *
 * Intl handles DST and half-hour offsets, needs no library and makes no network call. `ok` is
 * false for an unknown or malformed zone, and the caller renders nothing rather than 00:00 —
 * a clock that is confidently wrong is worse than no clock on a screen used to decide who to
 * telephone at 3am.
 *
 * The offset is derived from the SAME parts as the time, not from a second Intl call, so the
 * two can never disagree across a DST transition — a once-a-year, hours-wrong display on the
 * one screen where hours-wrong matters. It is computed arithmetically rather than read from
 * Intl's own offset string because that string is locale-shaped (ar-EG returns Arabic-Indic
 * digits, fr-FR a U+2212), and the band's horizontal clamp depends on a measurable width.
 */
function localParts(timeZone: string, at: Date) {
    if (!IANA_SHAPE.test(timeZone)) return FAILED_CLOCK
    try {
        const parts = new Intl.DateTimeFormat('en-GB', {
            timeZone, hourCycle: 'h23',
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
        }).formatToParts(at)
        const get = (t: string) => Number(parts.find((x) => x.type === t)?.value)
        const h = get('hour')
        const m = get('minute')
        if (!Number.isFinite(h) || !Number.isFinite(m)) return FAILED_CLOCK

        // Read the zone's wall clock back as if it were UTC; the difference is the offset.
        const asUTC = Date.UTC(get('year'), get('month') - 1, get('day'), h, m, get('second'))
        if (!Number.isFinite(asUTC)) return FAILED_CLOCK
        const offset = Math.round((asUTC - (at.getTime() - at.getMilliseconds())) / 60000)
        // Real offsets span UTC-12..UTC+14. Anything outside that is a parse that went wrong.
        if (!Number.isFinite(offset) || offset < -720 || offset > 840) return FAILED_CLOCK

        return { h, m, offset, ok: true as const }
    } catch {
        return FAILED_CLOCK
    }
}

/**
 * 'UTC+1', 'UTC-4', 'UTC+5:30' — never a place name.
 *
 * This replaced the tail of the IANA id ('Europe/London' -> 'London'), which named a city the
 * engineer had not hovered: Manchester read "London", and the same defect hit Toronto (New
 * York), Mumbai (Kolkata) and Beijing (Shanghai). An offset cannot name the wrong city because
 * it names no city at all, and it is the number actually needed here — every timestamp in the
 * product is UTC, so this is the bridge between a site's wall clock and the incident timeline.
 *
 * A city token was the panel's first answer and was rejected on evidence: WorldLocations.id=1
 * carries Tokyo's coordinates, Asia/Tokyo and a 35.6M population under city='Birmingham', and
 * a `city LIKE '%Tokyo%'` search returns nothing — so an admin adding a Tokyo site picks the
 * row displayed as "Birmingham, Japan" and the clock would have read "Birmingham · UTC+9".
 * That is the original complaint, reintroduced by the fix for it.
 *
 * U+2212 on screen rather than a hyphen: it has the same advance as '+' under tabular-nums and
 * is far more visible at 10px, and "UTC-4" misread as "UTC+4" is an eight-hour error. The
 * title and aria-label use ASCII so the value stays copyable.
 */
function offsetLabel(mins: number, minus = '−'): string {
    if (mins === 0) return 'UTC'
    const a = Math.abs(mins)
    const hh = Math.floor(a / 60)
    const mm = a % 60
    return `UTC${mins < 0 ? minus : '+'}${hh}${mm ? `:${String(mm).padStart(2, '0')}` : ''}`
}

/**
 * The widest label any zone can produce, for the hidden height sample.
 *
 * A literal, not a live lookup: the widest real offset belongs to Pacific/Chatham, whose own
 * offset moves with DST, so reserving from a zone id would make the band's geometry vary by
 * season.
 */
const WORST_CASE_LABEL = 'UTC+13:45'

/**
 * Parsed geometry, cached at module scope and keyed by tier.
 *
 * merge() on 1:10m takes ~450ms and the first geoPath another ~1.4s. Doing that in a render
 * pass locks the tab, and doing it per mount repeats it on every navigation.
 */
const geoCache = new Map<string, Promise<Geometry>>()

function loadGeometry(tier: 'medium' | 'high'): Promise<Geometry> {
    const cached = geoCache.get(tier)
    if (cached) return cached
    const promise = fetch(TIER[tier].file)
        .then((r) => {
            if (!r.ok) throw new Error(`geometry ${tier} ${r.status}`)
            return r.json()
        })
        .then((topo: any) => ({
            // merge for the fill, mesh for the borders. Stroking filled country polygons would
            // draw every shared border twice at double weight and make coast and border
            // indistinguishable.
            land: topojson.merge(topo, topo.objects.countries.geometries),
            borders: topojson.mesh(topo, topo.objects.countries, (a: any, b: any) => a !== b),
            graticule: geoGraticule10(),
        }))
        .catch((err) => {
            // Do not poison the cache: a failed fetch should be retried on the next mount.
            geoCache.delete(tier)
            throw err
        })
    geoCache.set(tier, promise)
    return promise
}


/**
 * Colour per location category, ASSIGNED from the categories actually present.
 *
 * This used to be a hardcoded map of five names. LocationCategory is a table anyone can add a
 * row to, and six categories had been added since — so AWS Data Centre and Azure Data Centre,
 * both in use, rendered grey as though they had no category at all.
 *
 * Assignment is by sorted position among the categories on the map, so it is stable for a
 * given estate and a new category gets a colour without a code change. Sorted rather than
 * insertion-ordered so the same estate always colours the same way.
 */
const CATEGORY_PALETTE_SIZE = 12

function buildCategoryColours(categories: string[]): Map<string, string> {
    const named = categories.map((c) => c.trim()).filter(Boolean).sort((a, b) => a.localeCompare(b))
    const out = new Map<string, string>()
    named.forEach((c, i) => out.set(c, `var(--cat-${(i % CATEGORY_PALETTE_SIZE) + 1})`))
    return out
}

/**
 * How far coincident markers are pushed apart, in pixels.
 *
 * A location and the vault serving it are often at the SAME coordinate — London Data Centre
 * and both its vaults are all at [-0.1167, 51.5] exactly. At 11px the markers touched and the
 * copy edge between them was a stub hidden underneath. 20 leaves the edge visible as a short
 * line, which is the truthful picture: the copy is real and the distance is zero.
 */
const COINCIDENT_RADIUS = 20

/**
 * Cluster markers that overlap on screen, by DISTANCE.
 *
 * This was a grid bucket — Math.round(x / 6) — and that fails at some zooms and not others:
 * two markers a pixel apart land in different buckets whenever they straddle a bucket
 * boundary, so a group that clustered at one zoom silently came apart at the next and the
 * vault ended up sitting on top of the location it serves. A grid quantises position; what
 * matters here is separation.
 *
 * Greedy single-pass: a point joins the first cluster whose centre it is within CLUSTER_PX of,
 * and the centre is re-averaged as members join. Good enough for tens of markers and stable —
 * the same input always gives the same grouping, which a hash-ordered pass would not.
 */
const CLUSTER_PX = 16

function groupCoincident<T extends { x: number; y: number }>(
    points: T[]
): Array<{ x: number; y: number; members: T[] }> {
    const clusters: Array<{ x: number; y: number; members: T[] }> = []
    // Sorted so the result does not depend on input order.
    const sorted = [...points].sort((a, b) => (a.y - b.y) || (a.x - b.x))
    for (const p of sorted) {
        const hit = clusters.find((c) => Math.hypot(c.x - p.x, c.y - p.y) <= CLUSTER_PX)
        if (hit) {
            hit.members.push(p)
            hit.x = hit.members.reduce((n, m) => n + m.x, 0) / hit.members.length
            hit.y = hit.members.reduce((n, m) => n + m.y, 0) / hit.members.length
        } else {
            clusters.push({ x: p.x, y: p.y, members: [p] })
        }
    }
    return clusters
}

/**
 * A copy edge as a screen-space path, sampled along the great circle.
 *
 * geoPath over a LineString would be simpler, but it projects from the raw coordinates and
 * the markers have been moved apart in screen space — so the line would stop short of the
 * marker it belongs to. Sampling the geodesic and projecting each sample keeps the curve and
 * the antimeridian break, while the two ends are pinned to where the markers actually are.
 *
 * The break test is a jump of more than a third of the canvas between consecutive samples,
 * which only happens when the shorter route crosses the date line.
 */
const SAMPLES = 36

function geodesicPath(
    from: [number, number],
    to: [number, number],
    a: { x: number; y: number },
    b: { x: number; y: number },
    projection: ReturnType<typeof geoMercator>,
    transform: ZoomTransform
): string {
    const interp = geoInterpolate(from, to)
    const pts: Array<[number, number] | null> = []
    for (let i = 0; i <= SAMPLES; i += 1) {
        const p = projection(interp(i / SAMPLES))
        pts.push(p ? [transform.applyX(p[0]), transform.applyY(p[1])] : null)
    }
    // Pin the ends to the markers; everything between keeps the curve.
    if (pts[0]) pts[0] = [a.x, a.y]
    if (pts[SAMPLES]) pts[SAMPLES] = [b.x, b.y]

    let d = ''
    let penDown = false
    let prev: [number, number] | null = null
    for (const p of pts) {
        if (!p) { penDown = false; prev = null; continue }
        const wrapped = prev && Math.abs(p[0] - prev[0]) > W * transform.k / 3
        if (!penDown || wrapped) {
            d += `M${p[0].toFixed(1)},${p[1].toFixed(1)}`
            penDown = true
        } else {
            d += `L${p[0].toFixed(1)},${p[1].toFixed(1)}`
        }
        prev = p
    }
    return d
}

type Hovered =
    | { kind: 'cluster'; locations: number; vaults: number; x: number; y: number }
    | { kind: 'location'; node: MapLocation; x: number; y: number }
    | { kind: 'vault'; node: MapVault; x: number; y: number }
    | { kind: 'copy'; copy: VaultCopy; from: MapLocation; to: MapVault; x: number; y: number }

/**
 * A Web Mercator world map as inline SVG.
 *
 * No tile engine, no API key, no per-load billing. Mercator specifically: it matches Google
 * Maps for the deferred handoff, and it keeps parallels and meridians straight.
 *
 * Layer visibility derives from the props, not a mode flag — pass locations and it is a
 * locations map, pass all three and it draws copies. A copy whose endpoints are not in the
 * supplied arrays is skipped silently, so a filtered screen does not have to prune its copy
 * list first.
 */
export function GlobalMap({
    locations = [],
    vaults = [],
    copies = [],
    detail = 'medium',
    height = 480,
    showLegend = true,
    showZoomControls = true,
    showPaletteControl = true,
    onSelect,
}: GlobalMapProps) {
    const svgRef = useRef<SVGSVGElement | null>(null)
    const wrapRef = useRef<HTMLDivElement | null>(null)
    const [geo, setGeo] = useState<Geometry | null>(null)
    /** Which tier's geometry is actually loaded — high renders at medium until it arrives. */
    const [loadedTier, setLoadedTier] = useState<'medium' | 'high'>('medium')
    const [transform, setTransform] = useState<ZoomTransform>(zoomIdentity)
    const [hovered, setHovered] = useState<Hovered | null>(null)
    /** Which coincident cluster the engineer has opened, if any. Key is its anchor pixel. */
    const [expanded, setExpanded] = useState<string | null>(null)

    /*
     * The hover clock.
     *
     * Deliberately its OWN state rather than being derived from `hovered`, for two reasons.
     * First it is pointer-only: markers set `hovered` on focus too, and a keyboard user tabbing
     * fourteen markers should not get fourteen clocks. Second, keeping it separate means the
     * band cannot break the tooltip — nothing here writes to `hovered`.
     *
     * Vaults never set it. A vault is an AWS region, not a staffed site, so its local time is
     * real but means nothing operationally. That asymmetry is intended.
     */
    const [clockLoc, setClockLoc] = useState<MapLocation | null>(null)
    const [nowTs, setNowTs] = useState<number>(() => Date.now())
    const barRef = useRef<HTMLDivElement | null>(null)
    const sampleRef = useRef<HTMLDivElement | null>(null)
    const clockRef = useRef<HTMLDivElement | null>(null)
    const tipRef = useRef<HTMLDivElement | null>(null)
    const [mapW, setMapW] = useState(0)
    const [bandH, setBandH] = useState(76)
    const [clockLeft, setClockLeft] = useState<number | null>(null)
    const [tipH, setTipH] = useState(0)
    const [face] = useState<ClockFace>(() => readClockFace())
    const [palette, setPalette] = useState<MapPalette>(() => readMapPalette())

    const togglePalette = useCallback(() => {
        setPalette((prev) => {
            const next: MapPalette = prev === 'theme' ? 'geographic' : 'theme'
            try { localStorage.setItem(PALETTE_KEY, next) } catch { /* preference only */ }
            return next
        })
    }, [])

    /**
     * Face diameter from the map's width.
     *
     * 64px at a 940px map, which is the size the dial is actually drawn for. It was specced at
     * 20-30px; at 26px both hands render under one device pixel (0.76 and 0.54) and become the
     * same grey line, which removes the only thing an analogue face tells you.
     */
    const faceD = Math.round(Math.max(48, Math.min(72, mapW * 0.068)))

    /** Tick only while a clock is shown. No seconds hand, so 15s is as fast as it can inform. */
    useEffect(() => {
        if (!clockLoc) return
        setNowTs(Date.now())
        const id = setInterval(() => setNowTs(Date.now()), 15000)
        return () => clearInterval(id)
    }, [clockLoc?.id])

    /**
     * Map width, which drives the face size and forces the band to be re-measured.
     *
     * THREE sources, deliberately, because relying on ResizeObserver alone means the clock
     * silently freezes at its first size if RO does not deliver — which is exactly what happened
     * on the rig this was verified on, where RO never fired even the initial callback it is
     * specified to. A frozen face is invisible until someone resizes and wonders why the dial
     * did not follow.
     *
     * The layout effect has no dependency array on purpose: it runs after every render and
     * corrects the width whenever anything else causes one. Setting state to a value it already
     * holds is a React bail-out, so this cannot loop.
     */
    useLayoutEffect(() => {
        const el = wrapRef.current
        if (!el) return
        const w = el.getBoundingClientRect().width
        setMapW((prev) => (Math.abs(prev - w) > 0.5 ? w : prev))
    })

    useEffect(() => {
        const el = wrapRef.current
        if (!el) return
        const measure = () => {
            const w = el.getBoundingClientRect().width
            setMapW((prev) => (Math.abs(prev - w) > 0.5 ? w : prev))
        }
        // A container that resizes without the window doing so (a panel, a split view).
        let ro: ResizeObserver | null = null
        try {
            ro = new ResizeObserver(measure)
            ro.observe(el)
        } catch { /* no RO: the layout effect and the window listener still cover it */ }
        window.addEventListener('resize', measure)
        measure()
        return () => {
            ro?.disconnect()
            window.removeEventListener('resize', measure)
        }
    }, [])

    /**
     * Reserve the band from a MEASURED worst case, not a formula.
     *
     * A multiple of the face under-measures, because the label lines and padding are not
     * proportional to it — a multiplier tuned at one size clips at another, and the symptom
     * (a sliced-off dial) reads as a positioning bug rather than a measurement one.
     */
    useLayoutEffect(() => {
        const el = sampleRef.current
        if (!el) return
        const h = el.getBoundingClientRect().height
        if (h > 0) setBandH(Math.max(34, Math.ceil(h) + 12))
    }, [faceD, face])

    // The clamp and tooltip-flip measurements live below toScreen(), which they depend on.

    /** Projection and path, fitted once. H comes from the fitted box, not from the height prop. */
    const { projection, path, H } = useMemo(() => {
        const proj = geoMercator().fitWidth(W, BOX as any)
        let p = geoPath(proj)
        const b = p.bounds(BOX as any)          // [[x0,y0],[x1,y1]] — nested, not flat
        const hh = Math.ceil(b[1][1] - b[0][1])
        proj.translate([proj.translate()[0], proj.translate()[1] - b[0][1]])
        p = geoPath(proj)
        return { projection: proj, path: p, H: hh }
    }, [])

    // Medium first, always. High is fetched lazily and swapped in when it resolves — 932 KB on
    // first paint for detail most sessions never zoom into is not worth it.
    useEffect(() => {
        let cancelled = false
        loadGeometry('medium').then((g) => { if (!cancelled) { setGeo(g); setLoadedTier('medium') } }).catch(() => {})
        return () => { cancelled = true }
    }, [])

    useEffect(() => {
        if (detail !== 'high') return
        let cancelled = false
        loadGeometry('high').then((g) => { if (!cancelled) { setGeo(g); setLoadedTier('high') } }).catch(() => {})
        return () => { cancelled = true }
    }, [detail])

    const tier = TIER[loadedTier]

    /**
     * Zoom on the SVG. Markers live outside the transform and are re-projected per event.
     *
     * The behaviour is kept in a ref because the +/- buttons MUST drive the same instance the
     * wheel does. Building a second one to call scaleBy on looks equivalent and is not: d3
     * dispatches zoom events on the behaviour's OWN listener registry, so a fresh instance sets
     * the node's internal transform and tells nobody. React never re-renders, the map does not
     * move, and the node's transform silently diverges from the transform state — which is
     * exactly how the buttons came to do nothing while the wheel kept working.
     */
    const zoomRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null)

    useEffect(() => {
        const svg = svgRef.current
        if (!svg) return
        const behaviour = d3zoom<SVGSVGElement, unknown>()
            .scaleExtent([1, 48])
            .translateExtent([[0, 0], [W, H]])
            .on('zoom', (event) => setTransform(event.transform))
        select(svg).call(behaviour)
        zoomRef.current = behaviour
        return () => { select(svg).on('.zoom', null); zoomRef.current = null }
    }, [H])

    const zoomBy = useCallback((k: number) => {
        const svg = svgRef.current
        const behaviour = zoomRef.current
        if (!svg || !behaviour) return
        select(svg).transition().duration(200).call(behaviour.scaleBy, k)
    }, [])

    /** Screen position of a coordinate under the current zoom. */
    const project = useCallback((coords: [number, number]): [number, number] | null => {
        const p = projection(coords)
        if (!p) return null
        return [transform.applyX(p[0]), transform.applyY(p[1])]
    }, [projection, transform])

    const plotted = useMemo(() => locations.filter((l) => Array.isArray(l.coords)), [locations])

    /**
     * Marker positions after spreading, computed across locations AND vaults together — a
     * location sitting on the vault that serves it is the common case, so separating each kind
     * on its own would leave them stacked.
     */
    const placedMarkers = useMemo(() => {
        const pts: Array<{ x: number; y: number; kind: 'location' | 'vault'; idx: number }> = []
        locations.forEach((l, idx) => {
            if (!Array.isArray(l.coords)) return
            const p = project(l.coords as [number, number])
            if (p) pts.push({ x: p[0], y: p[1], kind: 'location', idx })
        })
        vaults.forEach((v, idx) => {
            const p = project(v.coords)
            if (p) pts.push({ x: p[0], y: p[1], kind: 'vault', idx })
        })

        const groups = groupCoincident(pts)
        /*
         * TWO sets of positions, because two different questions are being answered.
         *
         * byLocation/byVault say WHERE TO DRAW A MARKER, and a member of a collapsed cluster
         * has no marker of its own — the cluster badge stands for it. Giving collapsed members
         * a drawing position (which is how the last change kept edges alive) rendered every
         * member on top of its own badge, so clusters looked broken.
         *
         * edgeLocation/edgeVault say WHERE AN EDGE ENDS, and every node needs one of those
         * whether or not it is drawn — a collapsed member's edge terminates at the badge.
         */
        const byLocation = new Map<number, { x: number; y: number }>()
        const byVault = new Map<number, { x: number; y: number }>()
        const edgeLocation = new Map<number, { x: number; y: number }>()
        const edgeVault = new Map<number, { x: number; y: number }>()
        const leaders: Array<{ x0: number; y0: number; x1: number; y1: number }> = []
        const clusters: Array<{ key: string; x: number; y: number; members: typeof pts }> = []

        for (const g of groups) {
            const key = `${Math.round(g.x)}:${Math.round(g.y)}`
            if (g.members.length === 1) {
                const m = g.members[0]
                ;(m.kind === 'location' ? byLocation : byVault).set(m.idx, { x: m.x, y: m.y })
                ;(m.kind === 'location' ? edgeLocation : edgeVault).set(m.idx, { x: m.x, y: m.y })
                continue
            }
            if (expanded !== key) {
                // Collapsed: one marker carrying the count. These nodes share a coordinate, so
                // any spread is a claim about distance that is not true — at world zoom a 20px
                // offset reads as several hundred miles.
                clusters.push({ key, x: g.x, y: g.y, members: g.members })
                // EDGE endpoints only — deliberately not marker positions. An edge into a
                // collapsed group terminates at the badge; the members themselves are not
                // drawn, because the badge is standing in for them.
                for (const m of g.members) {
                    (m.kind === 'location' ? edgeLocation : edgeVault).set(m.idx, { x: g.x, y: g.y })
                }
                continue
            }
            // Expanded on demand, with leaders back to the shared point.
            g.members.forEach((m, i) => {
                const angle = (i / g.members.length) * 2 * Math.PI - Math.PI / 2
                const x = g.x + Math.cos(angle) * COINCIDENT_RADIUS
                const y = g.y + Math.sin(angle) * COINCIDENT_RADIUS
                ;(m.kind === 'location' ? byLocation : byVault).set(m.idx, { x, y })
                ;(m.kind === 'location' ? edgeLocation : edgeVault).set(m.idx, { x, y })
                leaders.push({ x0: g.x, y0: g.y, x1: x, y1: y })
            })
        }
        return { byLocation, byVault, edgeLocation, edgeVault, leaders, clusters }
    }, [locations, vaults, project, expanded])
    const unlocated = useMemo(() => locations.filter((l) => !Array.isArray(l.coords)), [locations])
    /** The categories present on this map, in a stable order, for the legend. */
    /** Category -> colour, assigned from what is actually on this map. */
    const categoryColours = useMemo(
        () => buildCategoryColours(plotted.map((l) => String(l.category || ''))),
        [plotted]
    )
    const categoryColour = useCallback(
        (c?: string) => categoryColours.get(String(c || '').trim()) || 'var(--cat-none)',
        [categoryColours]
    )

    const categoriesShown = useMemo(() => {
        const seen = new Set<string>()
        for (const l of plotted) {
            const c = String(l.category || '').trim()
            // A blank category earns a legend row only if something on the map wears it —
            // and it is labelled, never left as a bare swatch.
            seen.add(c)
        }
        return [...seen].sort((a, b) => (a || '\uffff').localeCompare(b || '\uffff'))
    }, [plotted])

    const unlocatedObjects = useMemo(
        () => unlocated.reduce((n, l) => n + (l.objects || 0), 0),
        [unlocated]
    )

    /** Copies whose endpoints are both present. Anything else is skipped, silently and by design. */
    const drawnCopies = useMemo(() => {
        // Indices, not just objects: the spread positions are keyed on the index in the
        // ORIGINAL arrays, so an edge has to carry the same index to find its endpoint.
        const byLoc = new Map<string, { node: MapLocation; idx: number }>()
        locations.forEach((l, idx) => { if (Array.isArray(l.coords)) byLoc.set(l.id, { node: l, idx }) })
        const byVault = new Map<string, { node: MapVault; idx: number }>()
        vaults.forEach((v, idx) => byVault.set(v.id, { node: v, idx }))
        return copies
            .map((c) => ({ copy: c, f: byLoc.get(c.locationId), t: byVault.get(c.vaultId) }))
            .filter((e) => !!e.f && !!e.t)
            .map((e) => ({
                copy: e.copy,
                from: e.f!.node, fromIdx: e.f!.idx,
                to: e.t!.node, toIdx: e.t!.idx,
            }))
    }, [copies, locations, vaults])

    /**
     * Tooltip position.
     *
     * Read from the SVG's bounding rect scaled by the viewBox width, so it tracks correctly
     * when the container is responsive — the SVG's internal coordinates are not screen pixels.
     */
    const toScreen = useCallback((x: number, y: number) => {
        const svg = svgRef.current
        const wrap = wrapRef.current
        if (!svg || !wrap) return { x, y }
        const r = svg.getBoundingClientRect()
        const wr = wrap.getBoundingClientRect()
        // preserveAspectRatio="xMidYMid meet" letterboxes: the ELEMENT is full width but the
        // CONTENT is scaled to fit and centred. Scaling by r.width/W assumes the content fills
        // the element, which is only true when the box happens to match the viewBox ratio —
        // otherwise the tooltip drifts by half the letterbox, which is how it ended up on the
        // far side of the map from its marker.
        const scale = Math.min(r.width / W, r.height / H)
        const offX = (r.width - W * scale) / 2
        const offY = (r.height - H * scale) / 2
        return {
            x: r.left - wr.left + offX + x * scale,
            y: r.top - wr.top + offY + y * scale,
        }
    }, [H])

    /** SVG units -> screen px, for turning a marker's radius into a leader-line stop distance. */
    const screenScale = useCallback(() => {
        const svg = svgRef.current
        if (!svg) return 1
        const r = svg.getBoundingClientRect()
        return Math.min(r.width / W, r.height / H)
    }, [H])

    /**
     * Clamp the clock horizontally by measurement — the zone label is variable-length text
     * ("Los Angeles" against "Oslo"), so the clock's width is only knowable after it lays out.
     */
    useLayoutEffect(() => {
        if (!clockLoc) { setClockLeft(null); return }
        const bar = barRef.current
        const el = clockRef.current
        if (!bar || !el) return
        const bw = bar.getBoundingClientRect().width
        const half = el.getBoundingClientRect().width / 2
        const p = clockLoc.coords ? project(clockLoc.coords as [number, number]) : null
        const want = p ? toScreen(p[0], p[1]).x : bw / 2
        setClockLeft(Math.max(half + 4, Math.min(bw - half - 4, want)))
    }, [clockLoc, mapW, bandH, faceD, project, toScreen])

    /** Tooltip height, so a northern marker's tooltip can flip below instead of over the band. */
    useLayoutEffect(() => {
        const el = tipRef.current
        setTipH(el ? el.getBoundingClientRect().height : 0)
    }, [hovered])

    /** Where the clock sits and where its leader line lands, in mapwrap pixels. */
    const clockAnchor = useMemo(() => {
        if (!clockLoc || !Array.isArray(clockLoc.coords)) return null
        const p = project(clockLoc.coords as [number, number])
        if (!p) return null
        const s = toScreen(p[0], p[1])
        // Stop at the marker, not at the bottom of the canvas: the hit radius is 13 SVG units,
        // which is only 13 screen px when the container happens to match the viewBox ratio.
        return { x: s.x, y: s.y - 13 * screenScale() }
    }, [clockLoc, project, toScreen, screenScale])

    const clockTime = clockLoc?.timeZone ? localParts(clockLoc.timeZone, new Date(nowTs)) : null

    const activate = (node: MapLocation | MapVault, kind: 'location' | 'vault') => onSelect?.(node, kind)
    const onKey = (e: React.KeyboardEvent, node: MapLocation | MapVault, kind: 'location' | 'vault') => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(node, kind) }
    }

    const pad2 = (n: number) => String(n).padStart(2, '0')

    return (
        /*
         * Stage / band / mapwrap.
         *
         * The band is in NORMAL FLOW above the map, and every absolutely-positioned overlay
         * (tooltip, zoom controls, legend) is positioned against the mapwrap rather than the
         * stage. Positioning them against the stage would put the zoom controls on top of the
         * clock; mounting the SVG as the stage's first child would push the band below the map.
         *
         * `height` still means the height of the MAP, so adding the band grows the component
         * rather than shrinking the canvas — nothing that was visible before moves.
         */
        <div className="vs-map" data-palette={palette} style={{ height: height + bandH }}>
            <div ref={barRef} className="vs-map-clockbar" style={{ height: bandH }}>
                {/*
                 * The worst-case sample the band's height is measured from. Always mounted,
                 * never visible, never in the accessibility tree. This is the only approach
                 * that stays correct when the face is a design-system component whose rendered
                 * height this code does not know.
                 */}
                <div ref={sampleRef} className="vs-map-clock vs-map-clock-sample" aria-hidden>
                    <ClockDial hours={10} minutes={8} size={faceD} face={face} />
                    <div className="vs-map-clock-text">
                        <div className="vs-map-clock-time">00:00</div>
                        <div className="vs-map-clock-zone">{WORST_CASE_LABEL}</div>
                    </div>
                </div>

                {clockLoc && clockTime?.ok ? (
                    <div
                        ref={clockRef}
                        className="vs-map-clock"
                        style={{
                            left: clockLeft ?? 0,
                            // Measured, then revealed: the label is variable-length text, so the
                            // clamp cannot be computed until it has laid out.
                            visibility: clockLeft == null ? 'hidden' : 'visible',
                        }}
                    >
                        <ClockDial
                            hours={clockTime.h}
                            minutes={clockTime.m}
                            size={faceD}
                            face={face}
                            ariaLabel={`Local time at this location, ${pad2(clockTime.h)}:${pad2(clockTime.m)}, ${offsetLabel(clockTime.offset, '-')}, ${clockLoc.timeZone}`}
                        />
                        {/* The digital time belongs to the MAP, not the dial: a face this size is
                            not precisely readable and must not be the only carrier of the time. */}
                        <div className="vs-map-clock-text">
                            <div className="vs-map-clock-time">{pad2(clockTime.h)}:{pad2(clockTime.m)}</div>
                            {/* The offset, not a city. The IANA id stays in the title, where it is
                                the durable identifier — the offset is a LIVE reading and moves
                                with DST (Manchester is UTC+1 in August, UTC in November). */}
                            <div
                                className="vs-map-clock-zone"
                                title={`${clockLoc.timeZone} (${offsetLabel(clockTime.offset, '-')})`}
                            >
                                {offsetLabel(clockTime.offset)}
                            </div>
                        </div>
                    </div>
                ) : (
                    // A quiet line rather than nothing, so the reserved space reads as intentional.
                    <div className="vs-map-clock-idle">Hover a location for its local time</div>
                )}
            </div>

            <div ref={wrapRef} className="vs-map-wrap" style={{ height }}>
            <svg
                ref={svgRef}
                className="vs-map-canvas"
                viewBox={`0 0 ${W} ${H}`}
                preserveAspectRatio="xMidYMid meet"
                style={{ height: '100%' }}
                role="img"
                aria-label="World map"
            >
                <rect
                    width={W} height={H} fill="var(--ocean)"
                    onClick={() => setExpanded(null)}
                />

                {/* Map and copy layers ride the zoom transform; markers do not. */}
                <g transform={transform.toString()}>
                    {geo && (
                        <>
                            <path
                                d={path(geo.graticule) || ''}
                                fill="none"
                                stroke="var(--grat)"
                                strokeWidth={0.5}
                                vectorEffect="non-scaling-stroke"
                            />
                            <path d={path(geo.land) || ''} fill="var(--land)" stroke="none" />
                            <path
                                d={path(geo.borders) || ''}
                                fill="none"
                                stroke="var(--map-border)"
                                strokeWidth={tier.border}
                                strokeOpacity={tier.borderOpacity}
                                vectorEffect="non-scaling-stroke"
                            />
                            <path
                                d={path(geo.land) || ''}
                                fill="none"
                                stroke="var(--coast)"
                                strokeWidth={tier.coast}
                                strokeOpacity={tier.coastOpacity}
                                vectorEffect="non-scaling-stroke"
                            />
                        </>
                    )}

                </g>

                {/* Vault copies.
                    Drawn in SCREEN space, not inside the zoom transform, because the markers
                    are spread there — an edge projected from the raw coordinate ends 11px
                    short of the marker it belongs to, which is visible and looks like a bug.
                    The geodesic is preserved by sampling it and projecting each sample, so the
                    line still curves and still breaks at the antimeridian; only the endpoints
                    are pinned to where the markers actually ended up. */}
                {drawnCopies.map(({ copy, from, to, fromIdx, toIdx }) => {
                    const a = placedMarkers.edgeLocation.get(fromIdx)
                    const b = placedMarkers.edgeVault.get(toIdx)
                    if (!a || !b) return null
                    const d = geodesicPath(from.coords as [number, number], to.coords, a, b, projection, transform)
                    if (!d) return null
                    return (
                        <g key={`${copy.locationId}-${copy.vaultId}`}>
                            <path d={d} fill="none" stroke="var(--copy-line)" strokeWidth={1.25} strokeOpacity={0.65} />
                            {/* A 1px line is not hoverable. */}
                            <path
                                d={d}
                                fill="none"
                                stroke="transparent"
                                strokeWidth={12}
                                onMouseEnter={() => setHovered({
                                    kind: 'copy', copy, from, to,
                                    x: (a.x + b.x) / 2, y: (a.y + b.y) / 2,
                                })}
                                onMouseLeave={() => setHovered(null)}
                            />
                        </g>
                    )
                })}

                {/* Leader lines back to the true coordinate.
                    Drawn under the markers so a cluster reads as "these are all HERE", not as
                    several places a few kilometres apart. */}
                {placedMarkers.leaders.length > 0 && (
                    <g>
                        {placedMarkers.leaders.map((l, i) => (
                            <line
                                key={i}
                                x1={l.x0} y1={l.y0} x2={l.x1} y2={l.y1}
                                stroke="var(--map-border)"
                                strokeWidth={1}
                                strokeOpacity={0.8}
                            />
                        ))}
                        {[...new Map(placedMarkers.leaders.map((l) => [`${l.x0}:${l.y0}`, l])).values()].map((l, i) => (
                            <circle key={i} cx={l.x0} cy={l.y0} r={2} fill="var(--map-border)" />
                        ))}
                    </g>
                )}

                {/* Coincident nodes, collapsed to one marker with a count.
                    Click to fan them out; click again to collapse. These share a coordinate
                    exactly, so one marker is the truthful default and the fan is an explicit
                    request to see what is underneath. */}
                {placedMarkers.clusters.map((c) => {
                    const locs = c.members.filter((m) => m.kind === 'location').length
                    const vlts = c.members.length - locs
                    return (
                        <g
                            key={c.key}
                            className="vs-map-marker"
                            transform={`translate(${c.x},${c.y})`}
                            tabIndex={0}
                            role="button"
                            aria-label={`${c.members.length} at this point: ${locs} location(s), ${vlts} vault(s). Activate to separate them.`}
                            aria-expanded={false}
                            onClick={() => setExpanded(c.key)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(c.key) }
                            }}
                            // A cluster stands for several locations, so there is no single local
                            // time to show. Clear the band rather than pick one arbitrarily.
                            onMouseEnter={() => { setHovered({ kind: 'cluster', locations: locs, vaults: vlts, x: c.x, y: c.y }); setClockLoc(null) }}
                            onMouseLeave={() => setHovered(null)}
                            onFocus={() => setHovered({ kind: 'cluster', locations: locs, vaults: vlts, x: c.x, y: c.y })}
                            onBlur={() => setHovered(null)}
                        >
                            <circle className="vs-map-focus-ring" r={14} fill="none" opacity={0} />
                            <circle r={10} fill="var(--ocean)" stroke="var(--marker-vault)" strokeWidth={2} />
                            <text
                                textAnchor="middle"
                                dominantBaseline="central"
                                fontSize={10}
                                fontWeight={600}
                                /* Text, so 4.5:1 — not the 3:1 the marker shapes answer to.
                                   See --marker-text in map.css. */
                                fill="var(--marker-text)"
                                style={{ pointerEvents: 'none', userSelect: 'none' }}
                            >
                                {c.members.length}
                            </text>
                            <circle r={16} fill="transparent" />
                        </g>
                    )
                })}

                {/* Markers, re-projected each zoom so they keep a constant pixel size. */}
                {locations.map((loc, idx) => {
                    const p = placedMarkers.byLocation.get(idx)
                    if (!p) return null
                    const { x, y } = p
                    return (
                        <g
                            key={loc.id}
                            className="vs-map-marker"
                            transform={`translate(${x},${y})`}
                            tabIndex={0}
                            role="button"
                            aria-label={`${loc.name}, ${loc.place}, ${loc.objects} objects`}
                            onClick={() => activate(loc, 'location')}
                            onKeyDown={(e) => onKey(e, loc, 'location')}
                            onMouseEnter={() => { setHovered({ kind: 'location', node: loc, x, y }); setClockLoc(loc) }}
                            onMouseLeave={() => { setHovered(null); setClockLoc(null) }}
                            // Focus sets the tooltip but NOT the clock: pointer-only, so tabbing
                            // through the markers does not flash a clock per marker.
                            onFocus={() => setHovered({ kind: 'location', node: loc, x, y })}
                            onBlur={() => setHovered(null)}
                        >
                            <circle className="vs-map-focus-ring" r={13} fill="none" opacity={0} />
                            {/* Knockout ring in --ocean, never white: this is what keeps markers
                                legible on the dark theme. */}
                            <circle r={6.5} fill={categoryColour(loc.category)} stroke="var(--ocean)" strokeWidth={2} />
                            {/* A 5px target is hostile to hover. */}
                            <circle r={14} fill="transparent" />
                        </g>
                    )
                })}

                {vaults.map((v, idx) => {
                    const p = placedMarkers.byVault.get(idx)
                    if (!p) return null
                    const { x, y } = p
                    const s = 12.5
                    return (
                        <g
                            key={v.id}
                            className="vs-map-marker"
                            transform={`translate(${x},${y})`}
                            tabIndex={0}
                            role="button"
                            aria-label={`${v.name}, ${v.region}, ${v.status}, ${v.objects} objects`}
                            onClick={() => activate(v, 'vault')}
                            onKeyDown={(e) => onKey(e, v, 'vault')}
                            // A vault is an AWS region, not a staffed site: its local time is real
                            // but carries no operational meaning, so the band stays empty.
                            onMouseEnter={() => { setHovered({ kind: 'vault', node: v, x, y }); setClockLoc(null) }}
                            onMouseLeave={() => setHovered(null)}
                            onFocus={() => setHovered({ kind: 'vault', node: v, x, y })}
                            onBlur={() => setHovered(null)}
                        >
                            <circle className="vs-map-focus-ring" r={13} fill="none" opacity={0} />
                            <rect
                                x={-s / 2} y={-s / 2} width={s} height={s} rx={1.5}
                                fill="var(--ocean)"
                                stroke="var(--marker-vault)"
                                strokeWidth={2.5}
                                opacity={v.status === 'inactive' ? 0.5 : 1}
                            />
                            <circle r={14} fill="transparent" />
                        </g>
                    )
                })}
            </svg>

            {(showZoomControls || showPaletteControl) && (
                <div className="absolute right-2 top-2 flex flex-col gap-1">
                    {showZoomControls && (
                        <>
                            <button
                                type="button"
                                onClick={() => zoomBy(1.6)}
                                aria-label="Zoom in"
                                className="h-7 w-7 rounded border bg-background/90 text-sm leading-none shadow-sm hover:bg-muted"
                            >+</button>
                            <button
                                type="button"
                                onClick={() => zoomBy(1 / 1.6)}
                                aria-label="Zoom out"
                                className="h-7 w-7 rounded border bg-background/90 text-sm leading-none shadow-sm hover:bg-muted"
                            >−</button>
                        </>
                    )}
                    {showPaletteControl && (
                        /* Two swatches rather than an icon: the control shows what it will switch
                           TO, so it needs no label to be understood, and it reads correctly for
                           someone who cannot name the two palettes. */
                        <button
                            type="button"
                            onClick={togglePalette}
                            aria-pressed={palette === 'geographic'}
                            aria-label={palette === 'geographic'
                                ? 'Map colours: geographic. Switch to theme colours.'
                                : 'Map colours: theme. Switch to geographic colours.'}
                            title={palette === 'geographic' ? 'Geographic colours' : 'Theme colours'}
                            className="mt-1 flex h-7 w-7 items-center justify-center rounded border bg-background/90 shadow-sm hover:bg-muted"
                        >
                            <svg width="14" height="14" viewBox="0 0 14 14" className="vs-map-swatch" aria-hidden>
                                <circle cx="7" cy="7" r="6" fill="var(--ocean)" stroke="var(--coast)" strokeWidth="1" />
                                <path d="M7 1a6 6 0 0 1 0 12z" fill="var(--land)" />
                            </svg>
                        </button>
                    )}
                </div>
            )}

            {hovered && (() => {
                const s = toScreen(hovered.x, hovered.y)
                /*
                 * Flip below when placing it above would run it off the top of the map.
                 *
                 * Markers in northern latitudes sit near the top edge, and a tooltip anchored
                 * above its marker overflows the wrapper and lands on the clock band. tipH is 0
                 * on the first frame and measured on the next, so the worst case is one frame
                 * placed high, not a tooltip stuck in the wrong place.
                 */
                const above = s.y - 12 - tipH >= 4
                return (
                    <div
                        ref={tipRef}
                        className={`pointer-events-none absolute z-10 -translate-x-1/2 rounded-md border bg-popover px-2.5 py-1.5 text-xs shadow-md ${above ? '-translate-y-full' : ''}`}
                        style={{ left: s.x, top: above ? s.y - 12 : s.y + 14 }}
                        role="status"
                    >
                        {/* Kind first, always. Shape distinguishes a location from a vault on
                            the map, but a tooltip is read in isolation and a circle you are no
                            longer looking at is not a label. */}
                        {hovered.kind === 'cluster' && (
                            <>
                                <div className="font-medium">
                                    {hovered.locations + hovered.vaults} at this exact point
                                </div>
                                <div className="text-muted-foreground">
                                    {hovered.locations} location{hovered.locations === 1 ? '' : 's'} · {hovered.vaults} vault{hovered.vaults === 1 ? '' : 's'}
                                </div>
                                <div className="text-muted-foreground">Click to separate them</div>
                            </>
                        )}
                        {hovered.kind === 'location' && (
                            <>
                                <div className="flex items-center gap-1.5 font-medium">
                                    <span
                                        className="inline-block h-2 w-2 rounded-full"
                                        style={{ background: categoryColour(hovered.node.category) }}
                                        aria-hidden
                                    />
                                    Location
                                </div>
                                <div className="text-muted-foreground">{hovered.node.category || 'Uncategorised'}</div>
                                <div className="font-medium">{hovered.node.name}</div>
                                {hovered.node.place && (
                                    <div className="text-muted-foreground">{hovered.node.place}</div>
                                )}
                                <div className="text-muted-foreground">
                                    {hovered.node.objects} objects
                                    {hovered.node.engineers != null && (
                                        <> · {hovered.node.engineers} engineer{hovered.node.engineers === 1 ? '' : 's'}</>
                                    )}
                                </div>
                                {/* Recovery window, shown only when this map is showing a set. */}
                                {hovered.node.earliestStart && (
                                    <div className="text-muted-foreground">
                                        Starts {formatDateTime(hovered.node.earliestStart)}
                                    </div>
                                )}
                                {hovered.node.highestRta && (
                                    <div className="text-muted-foreground">
                                        Latest RTA {formatDateTime(hovered.node.highestRta)}
                                    </div>
                                )}
                            </>
                        )}
                        {hovered.kind === 'vault' && (
                            <>
                                <div className="flex items-center gap-1.5 font-medium">
                                    <span
                                        className="inline-block h-2 w-2 rounded-[1px] border-2"
                                        style={{ borderColor: 'var(--marker-vault)', background: 'var(--ocean)' }}
                                        aria-hidden
                                    />
                                    Vault
                                </div>
                                <div className="text-muted-foreground">
                                    {hovered.node.region || hovered.node.vaultId}
                                </div>
                                <div className="font-medium">{hovered.node.name}</div>
                                <div style={{ color: 'var(--marker-vault)' }}>{hovered.node.status}</div>
                                <div className="text-muted-foreground">
                                    {hovered.node.objects} objects · {hovered.node.versions} versions · {hovered.node.size}
                                </div>
                            </>
                        )}
                        {hovered.kind === 'copy' && (
                            <>
                                <div className="text-muted-foreground">Secured in vault</div>
                                <div className="font-medium">{hovered.from.name} → {hovered.to.name}</div>
                                {/* Real per-path measurements. They are shown, not scored — what
                                    counts as a good latency here has not been decided. */}
                                <div className="text-muted-foreground">
                                    {[
                                        hovered.copy.bandwidth != null ? `${hovered.copy.bandwidth} Mbps` : null,
                                        hovered.copy.latencyPercentage != null ? `${hovered.copy.latencyPercentage}% latency` : null,
                                        hovered.copy.tcpPercentage != null ? `${hovered.copy.tcpPercentage}% TCP` : null,
                                        hovered.copy.encryptionPercentage != null ? `${hovered.copy.encryptionPercentage}% encryption` : null,
                                    ].filter(Boolean).join(' · ') || '—'}
                                </div>
                            </>
                        )}
                    </div>
                )
            })()}

            {(showLegend || unlocated.length > 0) && (
                <div className="absolute bottom-2 left-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-md border bg-background/90 px-3 py-2 text-xs leading-none">
                    {/* One row per category actually on the map, not per category defined.
                        Five swatches for three categories in use is a legend that describes
                        the schema rather than the picture. */}
                    {showLegend && categoriesShown.map((cat) => (
                        <span key={cat || 'none'} className="flex items-center gap-1.5 whitespace-nowrap">
                            <svg width="10" height="10" viewBox="0 0 10 10" className="vs-map-swatch" aria-hidden>
                                <circle cx="5" cy="5" r="4" fill={categoryColour(cat)} />
                            </svg>
                            {cat || 'Uncategorised'}
                        </span>
                    ))}
                    {showLegend && vaults.length > 0 && (
                        <span className="flex items-center gap-1.5 whitespace-nowrap">
                            <svg width="10" height="10" viewBox="0 0 10 10" className="vs-map-swatch" aria-hidden>
                                <rect x="1" y="1" width="8" height="8" rx="1" fill="var(--ocean)" stroke="var(--marker-vault)" strokeWidth="2" />
                            </svg>
                            Vault
                        </span>
                    )}
                    {showLegend && drawnCopies.length > 0 && (
                        <span className="flex items-center gap-1.5 whitespace-nowrap">
                            <svg width="14" height="10" viewBox="0 0 14 10" className="vs-map-swatch" aria-hidden>
                                <line x1="0" y1="5" x2="14" y2="5" stroke="var(--copy-line)" strokeWidth="1.5" />
                            </svg>
                            Secured in vault
                        </span>
                    )}
                    {/* A map that silently omits two fifths of the estate is a worse answer to
                        "where are my objects" than the table already on the page. */}
                    {unlocated.length > 0 && (
                        <span className="whitespace-nowrap text-muted-foreground" title={unlocated.map((l) => l.name).join(', ')}>
                            {unlocatedObjects} objects unlocated
                        </span>
                    )}
                </div>
            )}

            {/* Leader from the band down to the marker. Runs diagonally when the clamp has
                pushed the clock away from the marker's longitude. */}
            {clockAnchor && clockLeft != null && clockTime?.ok && (
                <svg className="vs-map-leader" aria-hidden>
                    <line
                        x1={clockLeft} y1={0}
                        x2={clockAnchor.x} y2={clockAnchor.y}
                        stroke="var(--coast)"
                        strokeWidth={1}
                        strokeDasharray="3 3"
                    />
                </svg>
            )}
            </div>
        </div>
    )
}

export default GlobalMap
